if (!customElements.get('dd-collection-grid')) {
  class DDCollectionGrid extends HTMLElement {
    constructor() {
      super();
      this.filterController = null;
      this.loadMoreController = null;
      this.onChange = this.onChange.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onPopState = this.onPopState.bind(this);
    }

    connectedCallback() {
      if (this.isInitialized) return;
      this.isInitialized = true;
      this.classList.add('is-enhanced');
      this.addEventListener('change', this.onChange);
      this.addEventListener('submit', this.onSubmit);
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeyDown);
      document.addEventListener('click', this.onDocumentClick);
      window.addEventListener('popstate', this.onPopState);
      this.sanitizeInternalLinks();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('submit', this.onSubmit);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeyDown);
      document.removeEventListener('click', this.onDocumentClick);
      window.removeEventListener('popstate', this.onPopState);
      if (this.filterController) this.filterController.abort();
      if (this.loadMoreController) this.loadMoreController.abort();
      this.isInitialized = false;
    }

    onChange(event) {
      if (!event.target.matches('select[name="sort_by"], input[type="checkbox"], input[type="radio"], input[type="number"]')) {
        return;
      }

      const sortForm = event.target.closest('[data-dd-sort-form]');
      if (sortForm) {
        this.renderSortFromControl(event.target);
        return;
      }

      const form = event.target.closest('[data-dd-filter-form]');
      if (form) this.syncMirroredFilterControl(event.target, form);
      if (event.target.closest('.dd-collection-more')) return;
      if (form) this.renderFromForm(form, event.target);
    }

    onSubmit(event) {
      const sortForm = event.target.closest('[data-dd-sort-form]');
      if (sortForm) {
        event.preventDefault();
        const selectedSort = sortForm.querySelector('input[name="sort_by"]:checked');
        if (selectedSort) this.renderSortFromControl(selectedSort);
        return;
      }

      const form = event.target.closest('[data-dd-filter-form]');
      if (!form) return;
      event.preventDefault();
      this.renderFromForm(form, document.activeElement);
    }

    onClick(event) {
      const loadMore = event.target.closest('[data-dd-load-more]');
      if (loadMore && this.contains(loadMore)) {
        event.preventDefault();
        if (loadMore.classList.contains('is-loading')) return;
        this.loadMore(loadMore, event.detail === 0);
        return;
      }

      const clearFilters = event.target.closest('[data-dd-clear-filters]');
      if (clearFilters && this.contains(clearFilters)) {
        event.preventDefault();
        this.renderCollection(new URL(clearFilters.href, window.location.origin), true, clearFilters);
      }
    }

    onKeyDown(event) {
      if (event.key !== 'Escape') return;
      const openDetails = event.target.closest('details[open]');
      if (!openDetails || !this.contains(openDetails)) return;
      openDetails.removeAttribute('open');
      const summary = openDetails.querySelector(':scope > summary');
      if (summary) summary.focus();
    }

    onDocumentClick(event) {
      this.querySelectorAll('[data-dd-filter-details][open]').forEach((details) => {
        if (!details.contains(event.target)) details.removeAttribute('open');
      });
    }

    onPopState() {
      this.renderCollection(new URL(window.location.href), false);
    }

    renderFromForm(form, focusTarget) {
      const params = new URLSearchParams();
      const formData = new FormData(form);

      formData.forEach((value, key) => {
        const cleanValue = typeof value === 'string' ? value.trim() : value;
        if (cleanValue !== '' && !params.getAll(key).includes(String(cleanValue))) {
          params.append(key, cleanValue);
        }
      });

      params.delete('page');
      params.delete('section_id');

      const url = new URL(window.location.pathname, window.location.origin);
      url.search = params.toString();
      this.renderCollection(url, true, focusTarget);
    }

    syncMirroredFilterControl(control, form) {
      if (!control.matches('input[type="checkbox"], input[type="number"]') || !control.name) return;

      Array.from(form.elements).forEach((candidate) => {
        if (
          candidate === control ||
          candidate.name !== control.name ||
          candidate.type !== control.type ||
          (control.type === 'checkbox' && candidate.value !== control.value)
        ) {
          return;
        }

        if (control.type === 'checkbox') {
          candidate.checked = control.checked;
        } else {
          candidate.value = control.value;
        }
      });
    }

    renderSortFromControl(control) {
      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      url.searchParams.delete('section_id');
      url.searchParams.set('sort_by', control.value);
      this.renderCollection(url, true, control);
    }

    async renderCollection(publicUrl, updateHistory, focusTarget) {
      if (this.filterController) this.filterController.abort();
      if (this.loadMoreController) this.loadMoreController.abort();
      const controller = new AbortController();
      this.filterController = controller;

      const focusState = this.getFocusState(focusTarget);
      const requestUrl = new URL(publicUrl.href);
      requestUrl.searchParams.set('section_id', this.dataset.sectionId);
      this.setGridLoading(true);

      try {
        const response = await fetch(requestUrl.href, {
          headers: { Accept: 'text/html' },
          signal: controller.signal,
        });
        if (!response.ok) throw new Error('Collection request failed with status ' + response.status);

        const html = await response.text();
        const nextSection = this.getSectionFromHTML(html);
        if (!nextSection) throw new Error('Collection section was missing from the response');

        this.innerHTML = nextSection.innerHTML;
        this.sanitizeInternalLinks();

        if (updateHistory) {
          const browserUrl = publicUrl.pathname + publicUrl.search + publicUrl.hash;
          window.history.pushState({ ddCollectionGrid: this.dataset.sectionId }, '', browserUrl);
        }

        this.restoreFocus(focusState);
        this.initializeAddedContent(this);

        const count = this.querySelector('[data-dd-results-count]');
        if (count) this.announce(count.textContent.trim());
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('[DD collection grid]', error);
          this.announce(this.dataset.errorMessage || 'Something went wrong. Please try again.');
        }
      } finally {
        if (this.filterController === controller) {
          this.filterController = null;
          this.setGridLoading(false);
        }
      }
    }

    async loadMore(link, moveKeyboardFocus) {
      const controller = new AbortController();
      this.loadMoreController = controller;
      const label = link.querySelector('[data-dd-load-more-label]');
      const originalLabel = label ? label.textContent : '';
      const loadingLabel = link.dataset.loadingLabel || 'Loading…';
      link.classList.add('is-loading');
      link.setAttribute('aria-disabled', 'true');
      if (label) label.textContent = loadingLabel;

      const requestUrl = new URL(link.href, window.location.origin);
      requestUrl.searchParams.set('section_id', this.dataset.sectionId);

      try {
        const response = await fetch(requestUrl.href, {
          headers: { Accept: 'text/html' },
          signal: controller.signal,
        });
        if (!response.ok) throw new Error('Load more request failed with status ' + response.status);

        const html = await response.text();
        const nextSection = this.getSectionFromHTML(html);
        const currentGrid = this.querySelector('[data-dd-product-grid]');
        const nextGrid = nextSection ? nextSection.querySelector('[data-dd-product-grid]') : null;
        if (!currentGrid || !nextGrid) throw new Error('The next product page was missing its grid');

        const existingIds = new Set(
          Array.from(currentGrid.children).map((item) => item.getAttribute('data-product-id'))
        );
        const newItems = Array.from(nextGrid.children).filter(
          (item) => !existingIds.has(item.getAttribute('data-product-id'))
        );
        const firstNewLink = newItems.length ? newItems[0].querySelector('a') : null;
        newItems.forEach((item) => currentGrid.appendChild(item));

        const currentPagination = this.querySelector('[data-dd-pagination]');
        const nextPagination = nextSection.querySelector('[data-dd-pagination]');
        if (currentPagination && nextPagination) {
          currentPagination.replaceWith(nextPagination);
        } else if (currentPagination) {
          currentPagination.remove();
        }

        this.sanitizeInternalLinks();
        this.initializeAddedContent(currentGrid);

        const pagination = this.querySelector('[data-dd-pagination]');
        const loaded = pagination ? pagination.dataset.loadedCount : currentGrid.children.length;
        const total = pagination ? pagination.dataset.totalCount : loaded;
        this.announce(
          'Loaded ' + newItems.length + ' more products. Showing ' + loaded + ' of ' + total + '.'
        );

        if (moveKeyboardFocus && firstNewLink) firstNewLink.focus();
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('[DD collection grid]', error);
          link.classList.remove('is-loading');
          link.removeAttribute('aria-disabled');
          if (label) label.textContent = originalLabel;
          this.announce(this.dataset.errorMessage || 'Something went wrong. Please try again.');
        }
      } finally {
        if (this.loadMoreController === controller) this.loadMoreController = null;
        if (link.isConnected) {
          link.classList.remove('is-loading');
          link.removeAttribute('aria-disabled');
          if (label) label.textContent = originalLabel;
        }
      }
    }

    getSectionFromHTML(html) {
      const documentFromResponse = new DOMParser().parseFromString(html, 'text/html');
      return documentFromResponse.querySelector(
        'dd-collection-grid[data-section-id="' + this.dataset.sectionId + '"]'
      ) || documentFromResponse.querySelector('dd-collection-grid');
    }

    getFocusState(element) {
      if (!element || !this.contains(element)) return null;
      return {
        name: element.getAttribute('name'),
        value: element.value,
        tagName: element.tagName,
        controlLocation: element.dataset.ddControlLocation || null,
      };
    }

    restoreFocus(focusState) {
      if (!focusState || !focusState.name) return;
      const candidates = this.querySelectorAll('[name]');
      const match = Array.from(candidates).find(
        (element) =>
          element.getAttribute('name') === focusState.name &&
          (focusState.tagName === 'SELECT' || element.value === focusState.value) &&
          (!focusState.controlLocation ||
            element.dataset.ddControlLocation === focusState.controlLocation)
      );
      if (match) match.focus({ preventScroll: true });
    }

    setGridLoading(isLoading) {
      const content = this.querySelector('[data-dd-content]');
      const loading = this.querySelector('[data-dd-loading]');
      this.classList.toggle('is-loading', isLoading);
      if (content) content.setAttribute('aria-busy', String(isLoading));
      if (loading) loading.hidden = !isLoading;
    }

    sanitizeInternalLinks() {
      this.querySelectorAll('[data-dd-load-more]').forEach((link) => {
        const url = new URL(link.href, window.location.origin);
        url.searchParams.delete('section_id');
        link.href = url.pathname + url.search + url.hash;
      });
    }

    initializeAddedContent(root) {
      if (typeof window.initializeScrollAnimationTrigger === 'function') {
        window.initializeScrollAnimationTrigger(root);
      }
      document.dispatchEvent(
        new CustomEvent('dd:collection-grid:render', {
          detail: { sectionId: this.dataset.sectionId, root: root },
        })
      );
    }

    announce(message) {
      const status = this.querySelector('[data-dd-status]');
      if (!status) return;
      status.textContent = '';
      window.setTimeout(() => {
        status.textContent = message;
      }, 50);
    }
  }

  customElements.define('dd-collection-grid', DDCollectionGrid);
}
